/**
 * @lodex-pro/web-portal/routes/RequestorMatches — the admin's requestor matches (OPP PR 10).
 *
 * Haulers record who requests loads: their requestors, or the requester fields typed on a job.
 * On a pumper's or foreman's first sign-in those records are matched to them by email or phone.
 * A CONFLICT means email and phone point at two different hauler records for one person, or
 * one hauler record matches two people; the admin settles it with "Link" (this pairing is
 * right, which also dismisses the competing rows) or "Not them". Admin decisions survive a
 * re-run.
 *
 * Rendered on the admin's Contacts page (MyTanks.tsx). The API checks MANAGE_REQUESTOR_LINKS;
 * this is the UX, not the gate.
 */

import React from 'react';
import type { RequestorLinkView } from '@lodex-pro/shared/validation/operator-portal-routes';
import {
  useDecideRequestorLink,
  useRequestorLinks,
  useRerunRequestorMatch,
} from '../hooks/useRequestorLinks';
import { PortalDataError } from '../components/PortalDataError';
import { portalErrorText } from '../components/portal-error-text';
import { BUTTON, CARD, MUTED, TAG, plural } from './operator-tank-labels';

const EXPLAINER =
  'People your haulers record as requesting loads, matched by email or phone to your pumpers ' +
  'and foremen when they first sign in.';

const ERROR_TEXT: React.CSSProperties = { color: 'var(--color-danger)', fontSize: '13px' };
const CONFLICT_TAG: React.CSSProperties = {
  ...TAG,
  background: 'rgba(246, 182, 94, 0.14)',
  border: '1px solid rgba(246, 182, 94, 0.4)',
  color: 'var(--color-warning)',
};
const LINKED_TAG: React.CSSProperties = {
  ...TAG,
  background: 'rgba(111, 207, 142, 0.14)',
  color: 'var(--color-success)',
};

const STATUS_ORDER: Record<RequestorLinkView['status'], number> = {
  CONFLICT: 0,
  LINKED: 1,
  DISMISSED: 2,
};

const STATUS_TEXT: Record<RequestorLinkView['status'], string> = {
  CONFLICT: 'Conflict',
  LINKED: 'Linked',
  DISMISSED: 'Dismissed',
};

const MATCHED_ON_TEXT: Record<RequestorLinkView['matchedOn'], string> = {
  EMAIL: 'matched on email',
  PHONE: 'matched on phone',
  BOTH: 'matched on email and phone',
};

function roleText(role: string | null): string | null {
  if (role === 'PUMPER') return 'Pumper';
  if (role === 'FOREMAN') return 'Foreman';
  if (role === 'OPERATOR_ADMIN') return 'Admin';
  return null;
}

function personText(l: RequestorLinkView): string {
  const name = l.userName ?? l.userEmail ?? 'Unnamed';
  const role = roleText(l.userRole);
  return role === null ? name : `${name} (${role})`;
}

function requestorText(l: RequestorLinkView): string {
  if (l.requestorId === null) return `Requester typed on ${plural(l.jobCount, 'job')}`;
  const parts = [l.requestorName, l.requestorEmail, l.requestorPhone, l.requestorCompany].filter(
    (s): s is string => s !== null && s.trim() !== '',
  );
  return parts.length === 0 ? 'Unnamed requestor' : parts.join(' · ');
}

export function RequestorMatches(): React.JSX.Element {
  const links = useRequestorLinks(true);
  const decide = useDecideRequestorLink();
  const rerun = useRerunRequestorMatch();
  const [error, setError] = React.useState<string | null>(null);

  const rows: RequestorLinkView[] = [...(links.data?.links ?? [])].sort(
    (a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status],
  );
  const conflictCount = rows.filter((l) => l.status === 'CONFLICT').length;

  const onDecide = async (id: string, decision: 'LINK' | 'DISMISS'): Promise<void> => {
    setError(null);
    try {
      await decide.mutateAsync({ id, decision });
    } catch (err) {
      setError(portalErrorText(err, 'Could not save that decision.'));
    }
  };

  const onRerun = async (): Promise<void> => {
    setError(null);
    try {
      await rerun.mutateAsync();
    } catch (err) {
      setError(portalErrorText(err, 'Could not re-run the match.'));
    }
  };

  const busy = decide.isPending || rerun.isPending;

  return (
    <section aria-label="Requestor matches" style={{ ...CARD, marginTop: '24px' }}>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '4px' }}>
        <h2 style={{ fontSize: '16px', margin: 0 }}>Requestor matches</h2>
        {conflictCount > 0 && (
          <span style={CONFLICT_TAG} aria-label={`${conflictCount.toString()} to settle`}>
            {plural(conflictCount, 'conflict')}
          </span>
        )}
        <button
          type="button"
          style={{ ...BUTTON, marginLeft: 'auto' }}
          disabled={busy}
          onClick={(): void => {
            void onRerun();
          }}
        >
          Re-run match
        </button>
      </div>
      <p style={{ ...MUTED, fontSize: '13px', margin: '0 0 8px' }}>{EXPLAINER}</p>
      {error !== null && (
        <p role="alert" style={ERROR_TEXT}>
          {error}
        </p>
      )}
      {links.isLoading && <p role="status">Loading…</p>}
      {links.isError && (
        <PortalDataError error={links.error} fallback="Could not load the requestor matches." />
      )}
      {links.data !== undefined &&
        (rows.length === 0 ? (
          <p style={{ fontSize: '13px' }}>No matches yet.</p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {rows.map((l) => (
              <li
                key={l.id}
                aria-label={`Match ${personText(l)} — ${l.haulerName}`}
                style={{
                  padding: '8px 0',
                  borderTop: '1px solid var(--color-border-primary)',
                  fontSize: '14px',
                }}
              >
                <div
                  style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}
                >
                  <span style={{ fontWeight: 600 }}>{personText(l)}</span>
                  <span style={MUTED}>matches</span>
                  <span>
                    {l.haulerName}: {requestorText(l)}
                  </span>
                  <span
                    style={
                      l.status === 'CONFLICT'
                        ? CONFLICT_TAG
                        : l.status === 'LINKED'
                          ? LINKED_TAG
                          : TAG
                    }
                  >
                    {STATUS_TEXT[l.status]}
                  </span>
                  {(l.status === 'CONFLICT' || l.status === 'LINKED') && (
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
                      {l.status === 'CONFLICT' && (
                        <button
                          type="button"
                          style={BUTTON}
                          disabled={busy}
                          onClick={(): void => {
                            void onDecide(l.id, 'LINK');
                          }}
                        >
                          Link
                        </button>
                      )}
                      <button
                        type="button"
                        style={BUTTON}
                        disabled={busy}
                        onClick={(): void => {
                          void onDecide(l.id, 'DISMISS');
                        }}
                      >
                        Not them
                      </button>
                    </span>
                  )}
                </div>
                <div style={{ ...MUTED, fontSize: '12px', marginTop: '2px' }}>
                  {MATCHED_ON_TEXT[l.matchedOn]} · {plural(l.jobCount, 'job')}
                  {l.decidedByName !== null && <> · decided by {l.decidedByName}</>}
                </div>
              </li>
            ))}
          </ul>
        ))}
    </section>
  );
}
